import {Injectable} from '@angular/core';
import type {ThemePalette} from '@angular/material/core';
import {faCheckCircle, faHourglassHalf, faPlayCircle, faTimesCircle,} from '@fortawesome/free-solid-svg-icons';
import {CardHelperService} from 'libs/odin-core/src/lib/services/card-helper.service';
import moment from 'moment';
import {
  type IFormattedTransaction,
  type ITransaction,
  type ITransactionIcon,
  TransactionChannel,
  TransactionStatus,
  TransactionType,
} from '../models';
import {StaticHelpers} from '@odin/odin-core';
import { CountryConvertService } from '@odin/odin-core';

const VTOriginID = 'f3fff7e1-d9e3-4e59-a6f0-85682f96cd6e';
const PEBLOriginID = '1f5839a1-4d95-4de5-974d-6529f0aba976';

@Injectable()
export class TransactionFormatterService {
  constructor(
    private cardHelperService: CardHelperService,
    private countryMapper: CountryConvertService,
  ) {}

  formatTransaction(transaction: ITransaction): IFormattedTransaction {
    transaction.transactionType = this.getTransactionType(transaction);
    transaction.status = this.getTransactionStatus(transaction);

    // build detail about transaction before formatting
    const formattedTransaction = {
      ...transaction,
      ...{expiryDate: this.buildExpiry(transaction.expiryDate)},
      ...{isPreAuth: transaction.transactionType === 'pre-auth' || transaction.dispatch === 'LATER'},
      // Only default to the full amount when the backend didn't supply a
      // value — this used to unconditionally overwrite remainingValue with
      // the original amount, discarding any value the backend had already
      // computed and leaving refunds unreflected wherever the refund only
      // shows up as a TX360 event rather than a relatedTransactions entry.
      ...{remainingValue: transaction.remainingValue ?? transaction.amount},
      ...{transactionType: transaction.transactionType},
      ...{isReversal: transaction.transactionType === 'reversal'},
      ...{merchantCountry: transaction.countryCode},
      ...{ currencyCountry: this.resolveCurrencyCode(transaction) },
      ...{
        operatorId: StaticHelpers.isNullOrEmpty(transaction.operatorId)
          ? undefined
          : transaction.operatorId,
        operatorName: transaction.operatorName,
      },
    } as IFormattedTransaction;

    // format transaction with build detail
    return {
      ...formattedTransaction,
      ...{formattedChannel: this.tranasctionTypeToChannel(formattedTransaction)},
      ...{formattedType: this.getFormattedValue(formattedTransaction.transactionType)},
      ...this.getTransactionIcon(formattedTransaction),
      ...{
        formattedStatus: this.getFormattedValue(
          formattedTransaction.isReversed ? 'reversed' : formattedTransaction.status,
        ),
      },
    };
  }

  /**
   * Alpha currency code for display (the currency pipe needs "GBP", not "826").
   * Odin sends ISO numeric codes, the Portal BFF sends alpha codes, and either
   * field may carry it — so try both, in either notation.
   */
  private resolveCurrencyCode(transaction: ITransaction): string {
    const candidates = [transaction.currencyCode, transaction.currencyCountry];

    for (const candidate of candidates) {
      if (StaticHelpers.isNullOrEmpty(candidate)) continue;
      const value = candidate.trim();

      if (/^[A-Za-z]{3}$/.test(value)) {
        const alpha = value.toUpperCase();
        if (this.countryMapper.GetCountryByCurrencyCode(alpha)) return alpha;
      }

      if (/^\d{3}$/.test(value)) {
        const mapped = this.countryMapper.GetCountryByCurrencyNumericCode(value);
        if (mapped) return mapped.currencyCode;
      }
    }

    return 'CAD';
  }

  private buildExpiry(expiryDate: string): string | null {
    try {
      if (expiryDate === '') return null;
      if (expiryDate === null) return null;
      if (expiryDate === undefined) return null;
      if (expiryDate.length !== 4) return expiryDate; // unkown format
      return `${expiryDate.substring(2)}/${expiryDate.substring(0, 2)}`; // YYMM from server, we want MM/YY
    } catch {
      return expiryDate
    }
  }

  private tranasctionTypeToChannel(transaction: ITransaction): string {
    if (transaction.transactionType === TransactionType.VirtualTerminal)
      return 'Virtual Terminal';
    if (transaction.transactionType === TransactionType.PEBL)
      return 'Pay-By-Link';
    return this.getFormattedValue(transaction.channel);
  }

  private getTransactionType(transaction: ITransaction): TransactionType {
    if (transaction.transactionType === TransactionType.Sale) {
      if (transaction.originId == VTOriginID)
        return TransactionType.VirtualTerminal;
      if (transaction.originId == PEBLOriginID) return TransactionType.PEBL;
    }
    return transaction.transactionType;
  }

    public getTransactionStatus(transaction: ITransaction): TransactionStatus {
      if (transaction.dispatch === 'LATER' || transaction.isPreAuth) {
        if (transaction.status === TransactionStatus.Succeeded || transaction.status === TransactionStatus.PreAuthSuccess) {
          return TransactionStatus.PreAuthSuccess;
        } else {
          return TransactionStatus.PreAuthFailed;
        }
      }
      return transaction.status;
    }

  private getTransactionIcon(transaction: ITransaction): ITransactionIcon {
    const transactionStatus = transaction.status;
    const cardType = this.cardHelperService.GetCardType(transaction.maskedCard);
    const cardIcon = this.cardHelperService.GetCardIcon(cardType);

    switch (transactionStatus) {
      case TransactionStatus.PreAuthSuccess:
        return {
          statusIcon: faHourglassHalf,
          statusIconClass: 'transaction-pre-auth-succeeded',
          cardType: cardType,
          cardIcon: cardIcon,
        };
      case TransactionStatus.Succeeded:
        return {
          statusIcon: faCheckCircle,
          statusIconClass: transaction.isReversed
            ? 'transaction-reversed'
            : 'transaction-succeeded',
          cardType: cardType,
          cardIcon: cardIcon,
        };
      case TransactionStatus.Reversed:
        return {
          statusIcon: faCheckCircle,
          statusIconClass: 'transaction-reversed',
          cardType: cardType,
          cardIcon: cardIcon,
        };
      case TransactionStatus.AwaitingPaymentMethod:
      case TransactionStatus.AwaitingCapture:
      case TransactionStatus.Processing:
        return {
          statusIcon: faPlayCircle,
          statusIconClass: 'transaction-in-progress',
          cardType: cardType,
          cardIcon: cardIcon,
        };
      case TransactionStatus.PreAuthFailed:
        return {
          statusIcon: faHourglassHalf,
          statusIconClass: 'transaction-canceled',
          cardType: cardType,
          cardIcon: cardIcon,
        };
      case TransactionStatus.Cancelled:
        return {
          statusIcon: faTimesCircle,
          statusIconClass: 'transaction-canceled',
          cardType: cardType,
          cardIcon: cardIcon,
        };
      default:
        return {
          statusIcon: faPlayCircle,
          statusIconClass: 'transaction-in-progress',
          cardType: cardType,
          cardIcon: cardIcon,
        };
    }
  }

  public getFormattedValue(value: string): string {
    if (value === undefined) return value;
    return value
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  getStatusArray(): string[] {
    return Object.values(TransactionStatus).map((status) =>
      this.getFormattedValue(status),
    );
  }

  getChannelArray(): string[] {
    return Object.values(TransactionChannel).map((channel) =>
      this.getFormattedValue(channel),
    );
  }

  getTransactionBadgeClass(transactionStatus: TransactionStatus): string {
    switch (transactionStatus) {
      case TransactionStatus.PreAuthSuccess:
        return 'badge_pre_auth';
      case TransactionStatus.Succeeded:
        return 'badge_succeeded';
      case TransactionStatus.Reversed:
        return 'badge_succeeded';
      case TransactionStatus.AwaitingPaymentMethod:
      case TransactionStatus.Processing:
      case TransactionStatus.AwaitingCapture:
        return 'badge_info';
      case TransactionStatus.PreAuthFailed:
      case TransactionStatus.Cancelled:
        return 'badge_cancelled';
    }
  }

  getTransactionTimelineColour(
    transactionStatus: TransactionStatus,
  ): ThemePalette {
    switch (transactionStatus) {
      case TransactionStatus.Cancelled:
        return 'warn';
      default:
        return 'primary';
    }
  }

  getStepperIconColour(transactionStatus: TransactionStatus) {
    switch (transactionStatus) {
      case TransactionStatus.Cancelled:
        return 'cancelled';
      case TransactionStatus.Succeeded:
        return 'succeeded';
      default:
        return 'processing';
    }
  }

  convertToDate(date: moment.Moment | Date): Date {
    return date ? (moment.isMoment(date) ? date?.toDate() : date) : date;
  }

  // Hardcoded basic status array to enable filtering on basic status only
  getBasicStatusArray(): string[] {
    return ['Successful', 'Declined'];
  }
}

